import React, { useEffect, useState } from 'react';
import { Alert, Button, List, Modal, Tag, Typography } from 'antd';

import { QRUserRole } from '@totem/components/services/remoteAccess/v2/users/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';

const { Text } = Typography;

export type UserRoleRemoval = {
  user: QRUserRole;
  roleIds: string[];
};

type Props = {
  visible: boolean;
  users: QRUserRole[];
  confirming?: boolean;
  onConfirm: (removals: UserRoleRemoval[]) => void;
  onClose: () => void;
};

const RemoveUsersDialog = ({
  visible,
  users,
  confirming,
  onConfirm,
  onClose,
}: Props) => {
  const [pendingRoleIds, setPendingRoleIds] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    if (!visible) {
      return;
    }

    setPendingRoleIds(
      users.reduce<Record<string, string[]>>((acc, row) => {
        acc[row.user.user_id] = row.roles.map((role) => role.id);

        return acc;
      }, {}),
    );
  }, [visible, users]);

  const handleRemoveRole = (userId: string, roleId: string) => {
    setPendingRoleIds((current) => ({
      ...current,
      [userId]: (current[userId] ?? []).filter((id) => id !== roleId),
    }));
  };

  const removals: UserRoleRemoval[] = users
    .map((row) => ({
      user: row,
      roleIds: pendingRoleIds[row.user.user_id] ?? [],
    }))
    .filter((removal) => removal.roleIds.length > 0);

  const totalRoleRemovals = removals.reduce(
    (sum, removal) => sum + removal.roleIds.length,
    0,
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={
        <ModalTitle>
          Remove {users.length} User{users.length === 1 ? '' : 's'}
        </ModalTitle>
      }
      styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
      footer={
        <ModalFooter>
          <Button onClick={onClose} disabled={confirming}>
            Cancel
          </Button>
          <Button
            danger
            type="primary"
            disabled={totalRoleRemovals === 0}
            loading={confirming}
            onClick={() => onConfirm(removals)}
          >
            Remove{totalRoleRemovals > 0 ? ` (${totalRoleRemovals})` : ''}
          </Button>
        </ModalFooter>
      }
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Each user below will be removed from every role listed next to their name."
        description="Click the x on a role to keep that user in that role instead."
      />
      <List
        size="small"
        bordered
        dataSource={users}
        renderItem={(row) => {
          const remainingRoleIds = pendingRoleIds[row.user.user_id] ?? [];

          return (
            <List.Item>
              <List.Item.Meta
                title={row.user.name || row.user.email}
                description={
                  <>
                    <Text type="secondary">{row.user.email}</Text>
                    <br />
                    {row.roles
                      .filter((role) => remainingRoleIds.includes(role.id))
                      .map((role) => (
                        <Tag
                          key={role.id}
                          closable
                          onClose={(evt) => {
                            evt.preventDefault();

                            handleRemoveRole(row.user.user_id, role.id);
                          }}
                          style={{ marginTop: 4, marginRight: '4px' }}
                        >
                          {role.name}
                        </Tag>
                      ))}
                    {remainingRoleIds.length === 0 && (
                      <Text type="secondary" italic>
                        No roles selected for removal
                      </Text>
                    )}
                  </>
                }
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default RemoveUsersDialog;
